<template>
  <div>
    <div class="__header-container">
      <h4 class="__header-text">Tarif Harga</h4>
      <McButton
        v-if="!isCreatingContract && canEditFields"
        ref="add-button"
        text="Tambah Tarif"
        icon="icon-outline_plus"
        @click="handleAddRates"
        :disabled="isAdding"
      />
    </div>

    <b-card class="mt-3" v-if="isCreatingContract">
      <div class="empty-table-container">
        <img src="@/assets/images/no-data-illustration.png" alt />
        <div>
          <strong>Tarif Harga belum bisa ditambah</strong>
          <p>Simpan <strong>Kontrak</strong> terlebih dahulu</p>
        </div>
      </div>
    </b-card>

    <b-card class="mt-3" v-else-if="!isAdding && rates.length == 0">
      <div class="empty-table-container">
        <img src="@/assets/images/no-data-illustration.png" alt />
        <div>
          <strong>Belum ada data Tarif Harga</strong>
          <p>Tambahkan <strong>Tarif Data</strong> terlebih dahulu</p>

          <McButton text="Tambah Tarif" icon="icon-outline_plus" @click="handleAddRates" />
        </div>
      </div>
    </b-card>

    <template v-else v-for="(rate, rowIdx) in rates">
      <b-card class="mt-3" :key="rate.id">
        <div
          v-if="isLoading"
          style="background: white; opacity: 50%; position: absolute; inset: 0; z-index: 1"
        />

        <RatesForm
          v-if="isEditing(rowIdx) && isShowDataReady"
          :rowIdx="rowIdx"
          :addressFieldsGroups="addressFieldsGroups"
          :rateDetailFields="rateDetailFields"
          :rate="rate"
          :formValues.sync="formValues"
          @triggerSave="triggerSave"
          @triggerDelete="triggerDelete"
        />

        <RatesReadOnly
          v-else
          :rowIdx="rowIdx"
          :canEdit="editingRow != -1"
          :canEditFields="canEditFields"
          :addressFieldsGroups="addressFieldsGroups"
          :rateDetailFields="rateDetailFields"
          :rate="rate"
          :data="transformRatesShowResponse(rate)"
          @triggerEdit="triggerEdit"
        />

        <AdditionalCosts
          v-if="$store.state.config.contractVendorAdditionalCostFlag"
          :isAddingRates="false"
          :canEditFields="canEditFields"
          :additionalCosts="rate.additional_costs || []"
          :prefixUrl="{ ...prefixUrl, ratesId: rate?.id }"
        />
      </b-card>
    </template>

    <b-card class="mt-3 __blink" v-if="isAdding" ref="new-rates-form">
      <RatesForm
        :rowIdx="rates.length"
        :addressFieldsGroups="addressFieldsGroups"
        :rateDetailFields="rateDetailFields"
        :formValues.sync="formValues"
        @triggerSave="triggerSave"
        @triggerDelete="triggerDelete"
      />

      <AdditionalCosts 
        v-if="$store.state.config.contractVendorAdditionalCostFlag"
        :isAddingRates="true"
        :canEditFields="canEditFields"
        :additionalCosts="additionalCosts"
      />
    </b-card>
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import McButton from '@/components/McButton'
import * as Rates from '@/services/rest-api/lib/contract/rates'

import { EventBus } from '../lib/event-bus.js'
import { rateDetailFields, addressFieldsGroups } from '../lib/fields/rates-detail-fields'
import {
  transformRatesShowResponse,
  transformRatesStoreRequest,
  transformRatesUpdateRequest,
  transformAddressGroupByType,
} from '../lib/helpers/transform-data'
import RatesForm from './RatesForm.vue'
import RatesReadOnly from './RatesReadOnly.vue'
import AdditionalCosts from './AdditionalCosts.vue'

export default {
  components: {
    McButton,
    RatesForm,
    RatesReadOnly,
    // eslint-disable-next-line vue/no-unused-components
    AdditionalCosts,
  },
  props: {
    isCreatingContract: {
      type: Boolean,
      default: false,
    },
    canEditFields: {
      type: Boolean,
      default: false,
    },
    vendorId: {
      type: String,
      default: null,
    },
    contractId: {
      type: String,
      default: null,
    },
    versionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: true,

      formValues: {},
      showResponse: {},
      isShowDataReady: false,

      rates: [],
      editingRow: -1,
      isAdding: false,

      additionalCosts: [],
    }
  },
  computed: {
    rateDetailFields() {
      return rateDetailFields
    },
    // NOTE: can move this as internal state of RatesForm
    // To avoid wasting emits
    addressFieldsGroups() {
      return transformAddressGroupByType(this.formValues, addressFieldsGroups)
    },
    isEditing() {
      return (idx) => this.editingRow == idx
    },
    prefixUrl() {
      return {
        vendorId: this.vendorId,
        contractId: this.contractId,
        versionId: this.versionId,
      }
    },
  },
  watch: {
    '$route.params': 'listData',
    '$route.query': 'listData',
  },
  mounted() {
    if (!this.isCreatingContract) {
      this.listData()
    }

    // Handlers for when additional cost is merged with rates
    EventBus.$on('additionalCost:create', (data) => {
      this.additionalCosts.push(data)
    })

    EventBus.$on('additionalCost:update', (data) => {
      this.additionalCosts = this.additionalCosts.map((ac, idx) => (idx == data.id ? data : ac))
    })

    EventBus.$on('additionalCost:delete', (ids) => {
      this.additionalCosts = this.additionalCosts.filter((ac) => !ids.includes(ac.id))
    })

    EventBus.$on('additionalCost:deleteAll', () => {
      this.additionalCosts = []
    })
  },
  methods: {
    // Imported
    transformRatesShowResponse,

    // Core
    async listData() {
      const res = await Rates.get({
        vendorId: this.vendorId,
        contractId: this.contractId,
        versionId: this.versionId,
      })

      if (!res || res.errors) {
        this.$mcToast.error('Gagal mengambil data tarif harga')
        this.isLoading = false
        return false
      }

      this.rates = res.data?.paginated_result
      this.isLoading = false
    },
    async showData({ vendorId, contractId, versionId } = {}, ratesId) {
      this.isShowDataReady = false

      vendorId = vendorId ?? this.vendorId
      contractId = contractId ?? this.contractId
      versionId = versionId ?? this.versionId

      const res = await Rates.show({ vendorId, contractId, versionId }, ratesId)

      if (!res) {
        this.$mcToast.error('Gagal mengambil data tarif harga')
        this.isLoading = false
        return
      }

      if (res.errors) {
        this.$mcToast.error('Terdapat kesalahan pada input anda')
        this.isLoading = false
        return
      }

      this.showResponse = res.data
      this.formValues = transformRatesShowResponse(res.data)
      this.isShowDataReady = true
    },
    async handleAddRates() {
      this.isAdding = true
      await this.$nextTick()
      this.$refs['new-rates-form'].scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    // Utils
    async triggerEdit(idx) {
      this.isLoading = true

      this.editingRow = idx
      await this.showData({}, this.rates[idx].id)

      this.isLoading = false
    },

    async triggerSave(data) {
      this.isLoading = true

      let res = null

      if (this.isAdding) {
        // transform payload
        const transformed = transformRatesStoreRequest({
          ...data,
          additional_costs: this.additionalCosts,
        })

        res = await Rates.store(this.prefixUrl, transformed)
      } else {
        const ratesId = this.rates[this.editingRow].id
        const oldData = transformRatesShowResponse(this.showResponse)
        const diff = transformRatesUpdateRequest(data, oldData)
        if (isEmpty(diff)) {
          this.editingRow = -1
          this.isAdding = false
          this.isLoading = false
          return
        }

        res = await Rates.update(this.prefixUrl, ratesId, diff)
      }

      if (!res) {
        this.$mcToast.error('Gagal menyimpan data tarif harga')
        this.isLoading = false
        return
      }

      if (res.errors) {
        this.$mcToast.error('Terdapat kesalahan pada input anda')
        this.isLoading = false
        return
      }

      const isSuccess = await this.listData()
      if (isSuccess !== false) {
        this.$mcToast.success('Data berhasil disimpan')
      }

      this.editingRow = -1
      this.isAdding = false
      this.isLoading = false
      this.additionalCosts = []
    },

    async triggerDelete() {
      if (this.isAdding) {
        this.isAdding = false
        this.formValues = {}
        this.$refs['add-button'].$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      this.isLoading = true
      const res = await Rates.deleteSingle(this.prefixUrl, this.rates[this.editingRow].id)

      if (!res) {
        this.$mcToast.error('Gagal menghapus data tarif harga')
        this.isLoading = false
        return
      }

      if (res.errors) {
        this.$mcToast.error('Terdapat kesalahan pada input anda')
        this.isLoading = false
        return
      }

      const isSuccess = await this.listData()
      if (isSuccess !== false) {
        this.$mcToast.success('Data berhasil dihapus')
      }

      this.editingRow = -1
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
:deep() {
  .__base-price-container {
    background: $blue-100;
    padding: 1rem;
    margin: 0.5rem 0.3rem;
    border-radius: 4px;
  }

  .__base-price {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    line-height: 1em;

    h5 {
      margin: 0;
      margin-right: 1rem;
    }

    .validation {
      font-style: italic;
      color: $blue-300;

      strong {
        color: red;
        margin-right: 5px;
      }
    }
  }
}

.__blink {
  animation: blinker 0.5s linear 3;
  outline: 1px solid rgba($primary, 0.3);
  outline-offset: -1px;
}

@keyframes blinker {
  50% {
    opacity: 0.5;
  }
}
</style>
